import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import IconRadioButtonGroup from '@components/fields/IconRadioButtonGroup';
import styles from '@components/fields/RadioButtonGroupWithIcon.module.css';
import Form from '@components/forms/Form';
import {
  ClientRoleProviderEmotionalStateEnum,
  GetJourneyWithInitialConsultQuery,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { providerEmotionalStateIcon } from '@utils/modelUtils';

type FormData = {
  providerEmotionalState: ClientRoleProviderEmotionalStateEnum | null;
};

type Props = {
  client: GetJourneyWithInitialConsultQuery['journey']['owner'];
  consultId: string | undefined;
  initialConsultReportId: string;
  journeyId: string;
  onSuccess: (nextPage: string) => void;
};

const InitialConsultWellbeingForm = ({ client, consultId, initialConsultReportId, journeyId, onSuccess }: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  const clientRole = useMemo(
    () => client?.roles.find(role => role.journeyId === journeyId),
    [client?.roles, journeyId]
  );

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('stage');
      } else {
        onSuccess('social');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const initialValues = {
    providerEmotionalState: clientRole?.providerEmotionalState ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      providerEmotionalState: yup
        .string()
        .oneOf(Object.values(ClientRoleProviderEmotionalStateEnum))
        .nullable()
        .defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        id: initialConsultReportId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          setSubmitted(true);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [consultId, form, initialConsultReportId, intl, updateInitialConsultReport]);

  const providerEmotionalStateOptions = useMemo(() => {
    const options = [
      { value: ClientRoleProviderEmotionalStateEnum.very_negative },
      { value: ClientRoleProviderEmotionalStateEnum.negative },
      { value: ClientRoleProviderEmotionalStateEnum.neutral },
      { value: ClientRoleProviderEmotionalStateEnum.positive },
      { value: ClientRoleProviderEmotionalStateEnum.very_positive }
    ];
    return options.map(option => ({
      iconName: providerEmotionalStateIcon(option.value),
      label: intl.formatMessage({
        id: `models.pme.questions.provider_emotional_state.options.${option.value}.content`
      }),
      value: option.value
    }));
  }, [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className="ion-margin-top">
        <IconRadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.providerEmotionalState}
          label={intl.formatMessage({ id: 'models.pme.questions.provider_emotional_state.content' })}
          name="providerEmotionalState"
          options={providerEmotionalStateOptions}
        />
      </div>
      <div className={styles.spaceBetween}>
        <SecondaryButton
          data-test="submit-button"
          disabled={!form?.formState.isValid || form.formState.isSubmitting}
          onClick={() => setBackOrNext(false)}
          textKey="dictionary.previous"
          type="submit"
        />
        <PrimaryButton
          data-test="wellbeing-submit"
          disabled={!form?.formState.isValid || form.formState.isSubmitting}
          onClick={() => setBackOrNext(true)}
          textKey="dictionary.next"
          type="submit"
        />
      </div>
    </Form>
  );
};

export default InitialConsultWellbeingForm;
